<template>
  <div v-if="itemLoaded && routeDataLoaded">
    <b-row>
      <b-col>
        <h1 v-if="item.full_name">{{ item.full_name }}</h1>
        <h1 v-else>
          <em>{{ $tc("users.model_name", 1) | capitalize }}</em>
        </h1>
        <icon-button
          v-if="isGlobalAdmin && item.id"
          variant="outline-warning"
          size="sm"
          icon="person-badge"
          @click="mandate"
          >{{ $t("views.admin.user.login") }}</icon-button
        >
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <validation-observer ref="observer" v-slot="{ passes }">
          <b-form class="form" @submit.prevent="checkInvalidThenSubmit(passes)">
            <div class="form__section">
              <h2>{{ $t("views.admin.user.general.title") }}</h2>

              <div>
                <label>{{ $t("views.admin.user.general.account_created") }}</label>
                {{ item.created_at | date }}
              </div>
              <hr />

              <forms-builder
                v-model="item"
                :disabled="!canEditUser"
                :definition="form.general"
                entity="users"
                :hide-fields="
                  canEditUser
                    ? ['accept_conditions']
                    : [
                        'bank_transit_number',
                        'bank_institution_number',
                        'bank_account_number',
                        'accept_conditions',
                        'is_smart_phone',
                        'other_phone',
                      ]
                "
              >
                <template
                  #email="{
                    def,
                    descriptionOrNothing,
                    item: value,
                    placeholderIfExists,
                    property: key,
                  }"
                >
                  <b-row>
                    <b-col col md="9">
                      <forms-validated-input
                        v-bind="def"
                        v-model="value[key]"
                        :class="`forms-builder__field__${def.type}`"
                        :description="descriptionOrNothing(key)"
                        :label="$t(`users.fields.${key}`) | capitalize"
                        :name="key"
                        :disabled="!canEditUser || def.disabled"
                        :placeholder="placeholderIfExists(key)"
                      />
                    </b-col>

                    <b-col v-if="item && item.id && !item.email_verified_at" col md="3">
                      <b-form-group :label="$t('views.admin.user.verify_email_label')">
                        <b-button
                          :disabled="verificationLoading"
                          variant="primary"
                          @click="resendEmailVerification"
                          >{{ $t("views.admin.user.verify_email_button") }}</b-button
                        >
                      </b-form-group>
                    </b-col>
                  </b-row>
                </template>
              </forms-builder>
            </div>

            <div v-if="canManageGlobalRoles" class="form__section">
              <h2>{{ $t("views.admin.user.roles.title") }}</h2>

              <b-alert show variant="danger">
                {{ $t("views.admin.user.roles.global_admin_warning") }}
              </b-alert>

              <user-roles-list :user="item"> </user-roles-list>
            </div>

            <div v-if="canChangeUserPassword" class="form__section">
              <h2>{{ $t("views.admin.user.password.title") }}</h2>

              <user-password-form
                ref="passwordForm"
                :loading="loading"
                :required="false"
                :must-validate-current="loggedInUserIsMe"
                :user="item"
                @updated="onPasswordUpdated"
              />
            </div>

            <div v-if="canEditDriversProfile" class="form__section">
              <a id="borrower" />
              <h2>{{ $t("views.admin.user.driver.title") }}</h2>

              <p>
                <strong>{{
                  $t("colon", { text: $t("views.admin.user.driver.status.title") })
                }}</strong>
                {{ borrowerStatus }}
              </p>

              <p v-if="item.borrower && item.id" class="button-list align-items-start">
                <icon-button
                  v-if="!item.borrower.approved_at"
                  variant="outline-success"
                  :disabled="loading"
                  :onclick="approveBorrower"
                  icon="check2-square"
                >
                  {{ $t("users.approve") | capitalize }}
                </icon-button>
                <icon-button
                  v-else-if="!item.borrower.suspended_at"
                  variant="outline-warning"
                  :disabled="loading"
                  :onclick="suspendBorrower"
                  icon="pause-circle"
                >
                  {{ $t("users.suspend") | capitalize }}
                </icon-button>
                <icon-button
                  v-else
                  variant="outline-success"
                  :disabled="loading"
                  :onclick="unsuspendBorrower"
                  icon="play-circle"
                >
                  {{ $t("forms.reinstate") | capitalize }}
                </icon-button>

                <template v-if="item.borrower.submitted_at">
                  <div
                    v-if="!item.borrower.approved_at"
                    class="d-flex flex-column align-items-center"
                  >
                    <icon-button variant="ghost-danger" icon="person-x" :onclick="resetBorrower">
                      {{ $t("views.admin.user.driver.invalid.button") }}
                    </icon-button>
                    <small class="text-muted">{{
                      $t("views.admin.user.driver.invalid.note")
                    }}</small>
                  </div>
                  <icon-button v-else role="reset" :disabled="loading" :onclick="resetBorrower">
                    {{ $t("forms.reset") | capitalize }}
                  </icon-button>
                </template>
              </p>

              <forms-builder
                v-if="item.borrower"
                v-model="item.borrower"
                :definition="form.borrower"
                entity="borrowers"
              />
            </div>

            <div v-if="!!item.id" class="form__section">
              <paginated-table
                id="admin-user-loans-table"
                endpoint="loans"
                :fetch-params="{ forUser: item.id, for: 'admin' }"
                :columns="loanColumns"
                :extra-filters="loansExtraFilters"
                :load-relations="['loanable.active_incidents', 'loanable.merged_user_roles.user']"
                row-link
                :show-action-column="false"
              >
                <template #head>
                  <h2>{{ $tc("loans.model_name", 2) | capitalize }}</h2>
                </template>

                <template #cell(status)="{ item }">
                  <loan-status :show-self-service="false" :item="item"></loan-status>
                </template>
              </paginated-table>
            </div>

            <div v-if="!!item.id" class="form__section">
              <paginated-table
                id="admin-user-loanables-table"
                endpoint="loanables"
                :columns="loanableColumns"
                :fetch-params="{ owner_user_id: item.id, for: 'admin' }"
                :label="$tc('loanables.model_name', 2)"
                :show-action-column="false"
                row-link
                @row-selected="loanableRowSelected"
              />
            </div>

            <div v-if="!!item.id" id="communities" class="form__section">
              <community-users-list
                id="admin-user-communities-table"
                ref="communityTable"
                :fetch-params="{ 'user.id': item.id, for: 'admin' }"
                :user-id="item.id"
                :hide-fields="['user_id', 'user.full_name', 'updated_at', 'user.phone']"
                class="mb-3"
                :label="$tc('communities.community', 2)"
              />

              <forms-validated-input
                v-if="canEditUser"
                type="relation"
                name="community"
                :label="$t('views.admin.user.communities.add.label')"
                :value="null"
                reset-after-select
                :query="{
                  slug: 'communities',
                  value: 'id',
                  text: 'name',
                  params: {
                    '!users.id': item.id,
                    for: 'admin',
                  },
                }"
                @relation="addCommunity"
              />
            </div>

            <div v-if="!!item.id && canSeeUserBills" class="form__section">
              <paginated-table
                id="admin-user-invoices-table"
                endpoint="invoices"
                :fetch-params="{ user_id: item.id }"
                :columns="invoiceColumns"
                :show-generate-csv="false"
                :show-column-selection="false"
                :show-action-column="false"
                row-link
              >
                <template #head>
                  <h2>{{ $t("views.admin.user.account.title") }}</h2>
                  <p>
                    <strong>{{ $t("views.admin.user.account.balance") }}:</strong>
                    {{ item.balance | currency }}
                  </p>
                </template>

                <template #head-buttons>
                  <icon-button role="add" :to="`/admin/invoices/new?user_id=${item.id}`">
                    {{ $t("views.admin.user.account.add_invoice") }}
                  </icon-button>
                </template>
                <template #cell(total)="{ item }">
                  {{ getSignedAmount(item.type, item.total) | currency }}
                </template>
                <template #cell(total_with_taxes)="{ item }">
                  {{ getSignedAmount(item.type, item.total_with_taxes) | currency }}
                </template>
              </paginated-table>
            </div>

            <div v-if="!!item.id && canSeeUserBills" class="form__section">
              <paginated-table
                endpoint="payment_methods"
                :columns="paymentMethodColumns"
                :fetch-params="{ user_id: item.id }"
                :show-generate-csv="false"
                :show-action-column="false"
                :show-column-selection="false"
              >
                <template #head>
                  <h2>{{ $tc("paymentMethods.model_name", 2) | capitalize }}</h2>
                </template>

                <template #cell(type)="{ item }">
                  {{ $t(`users.payment_methods.types.${item.type}`) | capitalize }}
                </template>

                <template #cell(informations)="{ item }">
                  {{ item.credit_card_type }}
                  <span v-if="item.four_last_digits">
                    se terminant par {{ item.four_last_digits }}
                  </span>
                  ({{ item.external_id }})
                </template>
              </paginated-table>
            </div>

            <form-buttons v-if="canEditUser" :changed="changed" :saving="loading" @reset="reset" />
          </b-form>
        </validation-observer>
      </b-col>
    </b-row>
  </div>
  <layout-loading v-else />
</template>

<script>
import CommunityUsersList from "@/components/Community/CommunityUsersList.vue";
import FormsBuilder from "@/components/Forms/Builder.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import LoanStatus from "@/components/Loan/Status/LoanStatusBadge.vue";
import FormButtons from "@/components/shared/FormButtons.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import UserPasswordForm from "@/components/User/PasswordForm.vue";
import UserRolesList from "@/components/Admin/UserRolesList.vue";

import { capitalize } from "@/helpers/filters";
import { loanableLink, loanBorrowerLink, loanLoanableLink, userLink } from "@/helpers/links";
import {
  canAssignFleet,
  canChangeUserPassword,
  canEditDriversProfile,
  canEditUser,
  canManageGlobalRoles,
  canSeeDeletedUser,
  canSeeUserBills,
  isGlobalAdmin,
  userIsSame,
} from "@/helpers/permissions/users";

import DataRouteGuards from "@/mixins/DataRouteGuards";
import FormMixin from "@/mixins/FormMixin";
import dayjs from "dayjs";

export default {
  name: "AdminUser",
  components: {
    IconButton,
    FormButtons,
    LoanStatus,
    CommunityUsersList,
    PaginatedTable,
    FormsBuilder,
    FormsValidatedInput,
    UserPasswordForm,
    UserRolesList,
  },
  mixins: [DataRouteGuards, FormMixin],
  data() {
    const user = this.$store.state.user;
    return {
      invoiceColumns: [
        new Column("created_at", capitalize(this.$t("invoices.fields.date")), "date"),
        new Column("period", capitalize(this.$t("invoices.fields.name")), "text"),
        new Column("items_count", capitalize(this.$t("invoices.fields.items_count"))),
        new Column("total", capitalize(this.$t("invoices.totals.total")), "currency"),
        new Column(
          "total_with_taxes",
          capitalize(this.$t("invoices.totals.total_with_taxes")),
          "currency"
        ),
      ],
      loanablesSelected: [],
      loanableColumns: [
        new Column("id", capitalize(this.$t("lists.id")), "id"),
        new Column("name", capitalize(this.$t("loanables.fields.name")), "text", {
          urlFct: loanableLink,
        }),
        Column.withSelect("type", capitalize(this.$t("loanables.fields.type")), [
          { value: null, label: capitalize(this.$t("loanables.fields.types.all")) },
          { value: "car", label: capitalize(this.$t("loanables.fields.types.car")) },
          { value: "trailer", label: capitalize(this.$t("loanables.fields.types.trailer")) },
          { value: "bike", label: capitalize(this.$t("loanables.fields.types.bike")) },
        ]),
      ],
      loansExtraFilters: [
        new Filter("incidents.status", capitalize(this.$tc("incidents.model_name", 2)), "select", [
          { value: null, label: capitalize(this.$t("incidents.fields.status.options.null.all")) },
          {
            value: "in_process",
            label: capitalize(this.$t("incidents.fields.status.options.in_process")),
          },
          {
            value: "completed",
            label: capitalize(this.$t("incidents.fields.status.options.completed")),
          },
          {
            value: "canceled",
            label: capitalize(this.$t("incidents.fields.status.options.canceled")),
          },
        ]),
        new Filter(
          "extension_requested",
          this.$t("views.admin.user.loans.filters.extension.title"),
          "select",
          [
            { value: null, label: this.$t("views.admin.user.loans.filters.extension.options.all") },
            {
              value: true,
              label: this.$t("views.admin.user.loans.filters.extension.options.in_progress"),
            },
            {
              value: false,
              label: this.$t("views.admin.user.loans.filters.extension.options.none"),
            },
          ]
        ),
      ],
      loanColumns: [
        new Column("id", capitalize(this.$t("lists.id")), "id"),
        new Column("departure_at", capitalize(this.$t("loans.fields.departure_at")), "date", {
          formatter: (value, key, item) =>
            dayjs.atTz(value, item.loanable.timezone).format("D MMMM YYYY HH:mm"),
        }),
        new Column("actual_return_at", capitalize(this.$t("loans.fields.return_at")), "date", {
          showByDefault: false,
          formatter: (value, key, item) =>
            dayjs.atTz(value, item.loanable.timezone).format("D MMMM YYYY HH:mm"),
        }),
        Column.withRelation(
          "loanable.name",
          capitalize(this.$t("loans.fields.loanable")),
          {
            relation: "loanables",
            label: "name",
            field: "id",
            params: { for: "admin", with_deleted: true },
          },
          "loanable.id",
          {
            urlFct: loanLoanableLink,
          }
        ),
        Column.withRelation(
          "borrower_user.full_name",
          capitalize(this.$t("loans.fields.borrower")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { for: "admin", with_deleted: canSeeDeletedUser(user) },
          },
          "borrower_user.id",
          {
            urlFct: loanBorrowerLink,
          }
        ),
        Column.withRelation(
          "loanable.owner_user.full_name",
          capitalize(this.$t("loanables.fields.owner")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { for: "admin", with_deleted: canSeeDeletedUser(user) },
          },
          "loanable.owner_user_id",
          {
            urlFct: (loan) => userLink(loan.loanable.owner_user),
            sortable: false,
          }
        ),
        Column.withSelect("status", capitalize(this.$t("loans.fields.status")), [
          { value: null, label: capitalize(this.$t("loans.statuses.null.all")) },
          { value: "requested", label: this.$t("loans.statuses.requested.label") },
          { value: "accepted", label: this.$t("loans.statuses.accepted.label") },
          { value: "confirmed", label: this.$t("loans.statuses.confirmed.label") },
          { value: "ongoing", label: this.$t("loans.statuses.ongoing.label") },
          { value: "ended", label: this.$t("loans.statuses.ended.label") },
          { value: "validated", label: this.$t("loans.statuses.validated.label") },
          { value: "completed", label: this.$t("loans.statuses.completed.label") },
          { value: "rejected", label: this.$t("loans.statuses.rejected.label") },
          { value: "canceled", label: this.$t("loans.statuses.canceled.label") },
        ]),
        new Column(
          "duration_in_minutes",
          capitalize(this.$t("loans.fields.duration_in_minutes")),
          "number",
          {
            showByDefault: false,
          }
        ),
        new Column("calendar_days", capitalize(this.$t("loans.fields.calendar_days")), "number", {
          showByDefault: false,
        }),
        Column.withSelect(
          "loanable.type",
          capitalize(this.$t("loanables.fields.type")),
          [
            { value: null, label: capitalize(this.$t("loanables.fields.types.all")) },
            { value: "car", label: capitalize(this.$t("loanables.fields.types.car")) },
            {
              value: "trailer",
              label: capitalize(this.$t("loanables.fields.types.trailer")),
            },
            { value: "bike", label: capitalize(this.$t("loanables.fields.types.bike")) },
          ],
          { showByDefault: false }
        ),
        Column.withSelect(
          "is_self_service",
          capitalize(this.$t("loans.self_service.is_self_service")),
          [
            { value: null, label: capitalize(this.$t("loans.self_service.all")) },
            { value: true, label: capitalize(this.$t("loans.self_service.is_self_service")) },
            { value: false, label: capitalize(this.$t("loans.self_service.on_demand")) },
          ],
          { showByDefault: false }
        ),

        Column.withSelect(
          "alternative_to",
          this.$t("views.admin.user.loans.columns.replaced_transport"),
          [
            { value: null, label: this.$t("views.admin.user.loans.columns.transport_types.all") },
            {
              value: "delivery",
              label: this.$t("views.admin.user.loans.columns.transport_types.delivery"),
            },
            { value: "car", label: this.$t("views.admin.user.loans.columns.transport_types.car") },
            {
              value: "public_transit",
              label: this.$t("views.admin.user.loans.columns.transport_types.public_transit"),
            },
            {
              value: "bike",
              label: this.$t("views.admin.user.loans.columns.transport_types.bike"),
            },
            {
              value: "walking",
              label: this.$t("views.admin.user.loans.columns.transport_types.walking"),
            },
            {
              value: "other",
              label: this.$t("views.admin.user.loans.columns.transport_types.other"),
            },
          ],
          { showByDefault: false }
        ),
      ],
      paymentMethodColumns: [
        Column.withoutFilter("id", this.$t("lists.id"), "id", {
          sortable: false,
        }),
        Column.withoutFilter(
          "name",
          this.$t("views.admin.user.payment_methods.columns.name"),
          "text",
          { sortable: false }
        ),
        Column.withoutFilter(
          "informations",
          this.$t("views.admin.user.payment_methods.columns.info"),
          "text",
          { sortable: false }
        ),
      ],
      verificationLoading: false,
    };
  },
  computed: {
    borrowerStatus() {
      if (!this.item.borrower) {
        return this.$t("views.admin.user.driver.status.options.not_started");
      } else if (!this.item.borrower.approved) {
        return this.$t("views.admin.user.driver.status.options.not_approved");
      }
      return this.item.borrower.suspended
        ? this.$t("views.admin.user.driver.status.options.suspended")
        : this.$t("views.admin.user.driver.status.options.approved");
    },
    canAssignFleet() {
      return canAssignFleet(this.loggedInUser);
    },
    canChangeUserPassword() {
      return canChangeUserPassword(this.loggedInUser, this.item);
    },
    canEditUser() {
      return canEditUser(this.loggedInUser, this.item);
    },
    canEditDriversProfile() {
      return canEditDriversProfile(this.loggedInUser, this.item);
    },
    canManageGlobalRoles() {
      return canManageGlobalRoles(this.loggedInUser);
    },
    loggedInUser() {
      return this.$store.state.user;
    },
    loggedInUserIsMe() {
      return userIsSame(this.loggedInUser, this.item);
    },
    canSeeUserBills() {
      return canSeeUserBills(this.loggedInUser);
    },
    isGlobalAdmin() {
      return isGlobalAdmin(this.loggedInUser);
    },
  },
  methods: {
    async addCommunity(community) {
      if (!community) {
        return;
      }

      await this.$store.dispatch("communities/addUser", {
        userId: this.item.id,
        communityId: community.id,
      });
      this.$refs.communityTable.setFilters({ community_id: community.id });
    },
    afterSubmit() {
      this.$refs.passwordForm.reset();
    },
    async approveBorrower() {
      await this.$store.dispatch(`${this.slug}/approveBorrower`, this.item.id);
    },
    async checkInvalidThenSubmit(passes) {
      await passes(this.submit);

      const invalidItems = document.getElementsByClassName("is-invalid");
      if (invalidItems.length > 0) {
        invalidItems[0].scrollIntoView({
          behavior: "smooth",
        });
      }
    },
    loanableRowSelected(rows) {
      this.loanablesSelected = rows;
    },
    onPasswordUpdated() {
      this.$store.commit("addNotification", {
        title: this.$t("users.password_change.title"),
        variant: "success",
        content: this.$t("users.password_change.content"),
      });
    },
    async resendEmailVerification() {
      try {
        this.verificationLoading = true;
        await this.$store.dispatch("users/resendEmailVerification", this.item.id);
      } finally {
        this.verificationLoading = false;
      }
    },
    async suspendBorrower() {
      await this.$store.dispatch(`users/suspendBorrower`, this.item.id);
    },
    async unsuspendBorrower() {
      await this.$store.dispatch(`users/unsuspendBorrower`, this.item.id);
    },
    async resetBorrower() {
      await this.$store.dispatch(`users/resetBorrower`, this.item.id);
    },
    getSignedAmount(type, amount) {
      if (type === "debit") return -amount;

      return amount;
    },
    mandate() {
      this.$store.dispatch("account/mandate", { mandatedUserId: this.item.id });
    },
  },
};
</script>

<style lang="scss">
.borrower-button-list {
  align-items: flex-start;
}
</style>
